import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { formatReadingTime } from '@/components/ReadingTime';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Clock, 
  Loader2, 
  Trash2, 
  Star, 
  Lock,
  X,
  Calendar
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function History() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await api.get('/history');
      setHistory(res.data);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const removeFromHistory = async (storyId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setRemovingId(storyId);
    try {
      await api.delete(`/history/${storyId}`);
      setHistory(prev => prev.filter(s => s.id !== storyId));
      toast.success('Removido do histórico');
    } catch (error) {
      console.error('Error removing from history:', error);
      toast.error('Erro ao remover do histórico');
    } finally {
      setRemovingId(null);
    }
  };

  const clearAllHistory = async () => {
    setClearing(true);
    try {
      await api.delete('/history');
      setHistory([]);
      toast.success('Histórico limpo');
      setShowClearDialog(false);
    } catch (error) {
      console.error('Error clearing history:', error);
      toast.error('Erro ao limpar histórico');
    } finally {
      setClearing(false);
    }
  };

  const formatReadDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Hoje';
    } else if (diffDays === 1) {
      return 'Ontem';
    } else if (diffDays < 7) {
      return `Há ${diffDays} dias`;
    } else {
      return date.toLocaleDateString('pt-PT', { day: 'numeric', month: 'short' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="font-brand text-2xl font-bold text-foreground">Histórico de Leitura</h1>
                <p className="text-muted-foreground">{history.length} {history.length === 1 ? 'conto lido' : 'contos lidos'}</p>
              </div>
            </div>
            {history.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setShowClearDialog(true)}
                data-testid="clear-history-btn"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Limpar Histórico
              </Button>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-ruby" />
            </div>
          ) : history.length > 0 ? (
            <div className="space-y-3">
              {history.map(story => (
                <Link 
                  to={`/story/${story.slug || story.id}`} 
                  key={story.id}
                  data-testid={`history-card-${story.id}`}
                  className="group flex items-center gap-4 bg-surface rounded-xl border border-border p-4 hover:border-ruby/50 transition-all duration-300"
                >
                  {/* Cover with progress */}
                  <div className="relative w-16 h-22 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={story.cover_image || 'https://images.unsplash.com/photo-1605547446826-efe6b45ffe4d?w=400'}
                      alt={story.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    {story.is_premium && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-gold/90 rounded flex items-center justify-center">
                        <Lock className="w-3 h-3 text-foreground" />
                      </div>
                    )}
                    {/* Progress bar overlay */}
                    {story.progress > 0 && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/50">
                        <div 
                          className="h-full bg-ruby"
                          style={{ width: `${story.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-brand text-foreground font-semibold line-clamp-1 group-hover:text-ruby transition-colors">
                        {story.title}
                      </h3>
                      {story.progress > 0 && story.progress < 95 && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-ruby/20 text-ruby font-medium">
                          {story.progress}%
                        </span>
                      )}
                      {story.progress >= 95 && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 font-medium">
                          Concluído
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {story.author_name}
                    </p>
                    
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatReadingTime(story.reading_time)}
                      </span>
                      {story.rating > 0 && (
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-gold fill-gold" />
                          {story.rating.toFixed(1)}
                        </span>
                      )}
                      {story.read_at && (
                        <span className="flex items-center gap-1 text-ruby/70">
                          <Calendar className="w-3 h-3" />
                          {formatReadDate(story.read_at)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Continue Reading Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="hidden sm:flex text-ruby hover:bg-ruby/10"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/read/${story.slug || story.id}`);
                    }}
                    data-testid={`continue-reading-${story.id}`}
                  >
                    {story.progress > 0 && story.progress < 95 ? 'Continuar' : 'Ler'}
                  </Button>

                  {/* Remove Button */}
                  <button
                    onClick={(e) => removeFromHistory(story.id, e)}
                    disabled={removingId === story.id}
                    data-testid={`remove-history-${story.id}`}
                    className="p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    {removingId === story.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                  </button>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-ruby/10 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-ruby/50" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Nenhuma leitura ainda
              </h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Começa a ler contos e o seu histórico aparecerá aqui para que possa continuar de onde parou
              </p>
              <Button
                onClick={() => navigate('/browse')}
                data-testid="history-browse"
                className="bg-ruby hover:bg-ruby-light rounded-full"
              >
                Explorar Contos
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Clear All Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent className="bg-surface border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Limpar todo o histórico?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá remover todo o seu histórico de leitura ({history.length} {history.length === 1 ? 'conto' : 'contos'}). Esta ação não pode ser revertida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={clearAllHistory}
              disabled={clearing}
              data-testid="confirm-clear-history"
              className="bg-red-600 hover:bg-red-700"
            >
              {clearing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Limpar Histórico
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
